import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface RetentionRow {
  readonly retentionId: string;
  readonly retentionCode: string;
  readonly policyName: string;
  readonly tier: string;
  readonly retentionDays: number | null;
  readonly action: string;
  readonly legalHold: boolean;
  readonly lastAppliedAt: string | null;
  readonly status: string;
}

const TIERS = ['HOT', 'WARM', 'COLD', 'ARCHIVED', 'LEGAL_HOLD', 'COMPLIANCE'];
const ACTIONS = ['RETAIN', 'ARCHIVE', 'ANONYMIZE', 'REVIEW'];
const STATUSES = ['DRAFT', 'ACTIVE', 'SUSPENDED', 'ARCHIVED'];
const PAGE = 25;

/**
 * M41 — Retention Management Dashboard (§26 / §17). The retention surface over
 * the retention policies (T543). Define a retention policy across the six storage
 * tiers with a retention window, action and legal-hold flag, then apply it —
 * BR-05 keeps historical data accessible and BR-06 mandates retention policies
 * are enforced. Viewing needs RETENTION_VIEW, defining / applying needs
 * RETENTION_MANAGE.
 */
export function RetentionManagementDashboard(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RetentionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [legalHoldFilter, setLegalHoldFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [policyName, setPolicyName] = useState('');
  const [tier, setTier] = useState('HOT');
  const [retentionDays, setRetentionDays] = useState('');
  const [action, setAction] = useState('RETAIN');
  const [legalHold, setLegalHold] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDays, setEditDays] = useState('');
  const [editAction, setEditAction] = useState('RETAIN');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (tierFilter) params.set('tier', tierFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (legalHoldFilter) params.set('legalHold', legalHoldFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: RetentionRow[]; total: number }>(`/v1/data-lake/retention?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, tierFilter, statusFilter, legalHoldFilter, page, reload]);

  const define = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!policyName.trim()) { setActionError(t('m41.retention.nameRequired')); return; }
    const days = retentionDays.trim() === '' ? null : Number(retentionDays);
    if (days !== null && (!Number.isFinite(days) || days < 0)) { setActionError(t('m41.retention.daysNonNeg')); return; }
    try {
      await apiRequest('/v1/data-lake/retention', {
        method: 'POST',
        body: { policyName: policyName.trim(), tier, retentionDays: days, action, legalHold },
        token,
      });
      setNotice(t('m41.retention.defined'));
      setPolicyName('');
      setRetentionDays('');
      setLegalHold(false);
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.retention.notAuthDefine'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const apply = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/data-lake/retention/${id}/apply`, { method: 'POST', body: {}, token });
      setNotice(t('m41.retention.applied'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.retention.notAuthApply'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const startEdit = (r: RetentionRow): void => {
    setEditingId(r.retentionId);
    setEditDays(r.retentionDays == null ? '' : String(r.retentionDays));
    setEditAction(r.action);
    setActionError(null);
    setNotice(null);
  };

  const saveEdit = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    const days = editDays.trim() === '' ? null : Number(editDays);
    if (days !== null && (!Number.isFinite(days) || days < 0)) { setActionError(t('m41.retention.daysNonNeg')); return; }
    try {
      await apiRequest(`/v1/data-lake/retention/${id}`, { method: 'PUT', body: { retentionDays: days, action: editAction }, token });
      setNotice(t('m41.retention.updated'));
      setEditingId(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.retention.notAuthUpdate'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const remove = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/data-lake/retention/${id}`, { method: 'DELETE', token });
      setNotice(t('m41.retention.deleted'));
      if (editingId === id) setEditingId(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.retention.notAuthDelete'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="retention-management-dashboard">
      <h1>{t('m41.retention.title')}</h1>

      <section aria-label="retention-define">
        <h2>{t('m41.retention.definePolicy')}</h2>
        <form aria-label="define-form" onSubmit={(e) => void define(e)}>
          <label>{t('m41.retention.policyName')}<input aria-label="field-policy-name" value={policyName} onChange={(e) => setPolicyName(e.target.value)} /></label>
          <label>{t('m41.retention.tier')}
            <select aria-label="field-tier" value={tier} onChange={(e) => setTier(e.target.value)}>
              {TIERS.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m41.retention.retentionDays')}<input aria-label="field-retention-days" type="number" min="0" value={retentionDays} onChange={(e) => setRetentionDays(e.target.value)} /></label>
          <label>{t('m41.retention.action')}
            <select aria-label="field-action" value={action} onChange={(e) => setAction(e.target.value)}>
              {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label>{t('m41.retention.legalHold')}<input aria-label="field-legal-hold" type="checkbox" checked={legalHold} onChange={(e) => setLegalHold(e.target.checked)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-define">{t('m41.common.define')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="retention-action-error">{actionError}</p>}
        {notice && <p aria-label="retention-notice">{notice}</p>}
      </section>

      <section aria-label="retention-filters">
        <label>{t('m41.retention.tier')}
          <select aria-label="filter-tier" value={tierFilter} onChange={(e) => { setTierFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {TIERS.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m41.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>{t('m41.retention.legalHold')}
          <select aria-label="filter-legal-hold" value={legalHoldFilter} onChange={(e) => { setLegalHoldFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            <option value="true">{t('common.yes')}</option>
            <option value="false">{t('common.no')}</option>
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="retention-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="retention-total">{t('m41.retention.total', { count: total })}</p>
          <DataTable
            ariaLabel="retention-table"
            rows={rows}
            rowKey={(r) => r.retentionId}
            emptyMessage={t('m41.retention.empty')}
            columns={[
              { key: 'code', header: t('m41.common.code'), cellAriaLabel: (r) => `retention-code-${r.retentionId}`, render: (r) => r.retentionCode },
              { key: 'policy', header: t('m41.retention.policy'), render: (r) => r.policyName },
              { key: 'tier', header: t('m41.retention.tier'), render: (r) => r.tier },
              { key: 'days', header: t('m41.retention.days'), render: (r) => r.retentionDays ?? '—' },
              { key: 'action', header: t('m41.retention.action'), render: (r) => r.action },
              { key: 'legalHold', header: t('m41.retention.legalHold'), render: (r) => r.legalHold ? t('common.yes') : t('common.no') },
              { key: 'lastApplied', header: t('m41.retention.lastApplied'), render: (r) => at(r.lastAppliedAt) },
              { key: 'status', header: t('m41.common.status'), cellAriaLabel: (r) => `retention-status-${r.retentionId}`, render: (r) => r.status },
              { key: 'actions', header: t('m41.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`apply-${r.retentionId}`} onClick={() => void apply(r.retentionId)}>{t('m41.retention.apply')}</Button>
                  {editingId === r.retentionId ? (
                    <>
                      <input aria-label={`edit-days-${r.retentionId}`} type="number" min="0" value={editDays} onChange={(e) => setEditDays(e.target.value)} />
                      <select aria-label={`edit-action-${r.retentionId}`} value={editAction} onChange={(e) => setEditAction(e.target.value)}>
                        {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <Button variant="primary" type="button" aria-label={`save-${r.retentionId}`} onClick={() => void saveEdit(r.retentionId)}>{t('common.save')}</Button>
                      <Button variant="danger" type="button" aria-label={`cancel-${r.retentionId}`} onClick={() => setEditingId(null)}>{t('common.cancel')}</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="secondary" type="button" aria-label={`edit-${r.retentionId}`} onClick={() => startEdit(r)}>{t('m41.common.edit')}</Button>
                      <Button variant="danger" type="button" aria-label={`delete-${r.retentionId}`} onClick={() => void remove(r.retentionId)}>{t('common.delete')}</Button>
                    </>
                  )}
                </>
              ) },
            ]}
          />

          <nav aria-label="retention-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m41.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m41.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m41.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
